import classNames from 'classnames';
import * as React from 'react';
import { ProductPricesCollection } from 'palette/candidates/ProductPrices/types';
import { useProductCardContext } from 'palette/ProductCard';
import { CARD_THEMES } from 'palette/ProductCard/constants';
import {
    IColors,
    IPrices,
    Thumbnail,
    ISingleItemData,
} from 'palette/ProductCard/types/productData';
import { BodyText, DEFAULT_TAG_NAME, TagName } from 'palette/Typography';
import ActionButtons from './ActionButtons';
import Colors from './Colors';
import {
    DescriptionHover, Prices, SingleItemDetails, PriceWrapper,
} from './Description/';
import SizeList from './SizeList';

import '../styles/Description.scss';

interface IDescriptionData {
    prices: IPrices;
    sizes: Array<string>;
    singleItemData?: ISingleItemData;
    isFavorite?: boolean;
}

export type DescriptionProps = React.PropsWithChildren<{
    prices: IPrices;
    productPricesStructure: Record<number | string, ProductPricesCollection[]>;
    colors: IColors;
    descriptionData: IDescriptionData;
    thumbnails: Thumbnail[];
    brandName?: string;
    productName?: string | null;
    children?: React.ReactNode;
    actionButtons?: React.ReactElement[];
    favoriteButton?: React.ReactElement;
    brandnameFieldTagname?: TagName;
    hasToShowFavoriteButtonLast?: boolean;
}>;

function Description({
    prices,
    productPricesStructure,
    colors,
    descriptionData,
    thumbnails,
    brandName,
    productName,
    children,
    actionButtons,
    favoriteButton,
    brandnameFieldTagname = DEFAULT_TAG_NAME.span,
    hasToShowFavoriteButtonLast = false,
}: DescriptionProps) {
    const {
        orientation: cardOrientation,
        size: cardSize,
        showSizes,
        floatPriceToRight,
        hasHoverEffect,
        isHovered,
        actionButtonsPosition,
        isSelectable,
        activeVariantId,
        theme,
    } = useProductCardContext();

    const pricesStructure: ProductPricesCollection[] = productPricesStructure[activeVariantId];
    const hasActionButton = !!actionButtons?.length || favoriteButton;
    const isVerticalCard = cardOrientation === 'vertical';
    const hasToShowActionButtons = !isSelectable
        && actionButtonsPosition !== 'image'
        && hasActionButton;

    const { sizes, singleItemData } = descriptionData;

    const titleRowClasses = classNames('palette-product-card-description__title-row', {
        'palette-product-card-description__title-row--has-favorite-button': actionButtonsPosition === 'float',
    });

    return (
        <div className={classNames('palette-product-card-description', {
            [`palette-product-card-description--${cardOrientation}`]: cardOrientation,
            [`palette-product-card-description--${cardSize}`]: cardSize,
        })}
        >
            <div className={classNames('palette-product-card-description__content', {
                [`palette-product-card-description__content--${cardOrientation}`]: cardOrientation,
                [`palette-product-card-description__content--${cardSize}`]: cardSize,
                'palette-product-card-description__content--hidden-on-hover': hasHoverEffect && isHovered,
            })}
            >
                <BodyText
                    className={titleRowClasses}
                    color={theme === CARD_THEMES.dark ? 'white' : 'strong'}
                    tagName={brandnameFieldTagname}
                    {...isVerticalCard && {
                        overflowType: 'ellipsis',
                    }}
                    level="1"
                >
                    {brandName?.replace(/\s\s+/g, '')}
                </BodyText>
                {productName && (
                    <BodyText
                        className={titleRowClasses}
                        color={theme === CARD_THEMES.dark ? 'white' : 'light'}
                        {...isVerticalCard && {
                            overflowType: 'ellipsis',
                        }}
                    >
                        {productName?.replace(/\s\s+/g, '')}
                    </BodyText>
                )}
                {
                    singleItemData && <SingleItemDetails data={singleItemData} />
                }
                {
                    !floatPriceToRight && pricesStructure && (
                        <PriceWrapper
                            bemModifier={cardSize}
                            pricesStructure={pricesStructure}
                        />
                    )
                }
                {
                    !floatPriceToRight && !pricesStructure && (
                        <Prices
                            bemModifier={cardSize}
                            prices={prices[activeVariantId]}
                        />
                    )
                }
                <div className="palette-product-card-description__content__colors-sizes">
                    <Colors
                        colors={colors}
                        showInline={!singleItemData}
                    />
                    {
                        showSizes && (
                            <SizeList
                                isInline
                                sizes={sizes}
                            />
                        )
                    }
                </div>
                {children || null}
            </div>
            {hasHoverEffect && thumbnails.length > 1 && isHovered && (
                <DescriptionHover
                    colors={colors}
                    prices={prices}
                    pricesStructure={pricesStructure}
                    thumbnails={thumbnails}
                />
            )}
            {
                floatPriceToRight && pricesStructure && (
                    <PriceWrapper
                        bemModifier={cardSize}
                        pricesStructure={pricesStructure}
                    />
                )
            }
            {
                floatPriceToRight && !pricesStructure && (
                    <Prices
                        floatPriceToRight
                        bemModifier={cardSize}
                        prices={prices[activeVariantId]}
                    />
                )
            }
            {
                hasToShowActionButtons && (
                    <ActionButtons
                        bemModifier="description"
                        buttons={actionButtons}
                        favoriteButton={favoriteButton}
                        hasToShowFavoriteButtonLast={hasToShowFavoriteButtonLast}
                    />
                )
            }

        </div>
    );
}

export default Description;
